import { Button, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { API } from 'core/requests';
import { useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { useCompany } from 'contexts';
import { DialogTeleport, MoneyInput } from 'components';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { get } from 'lodash';
import { budgetLoadingInitialState, budgetAddBankFormInitialState, budgetVerifyBankFormInitialState, budgetAccountHolderTypes } from 'constants/budget';

type LoadingTypes = {
	addBankLoading: boolean;
	addAmountLoading: boolean;
	verifyBankLoading: boolean;
};

const validationSchema = yup.object({
	account_holder_name: yup.string().required("This field is Required"),
	account_holder_type: yup.string().required("This field is Required"),
	account_number: yup.number().required("This field is Required"),
	routing_number: yup.number().required("This field is Required"),
});

const ManageAddBankAccount = ({ openAddBank, setOpenAddBank, stripePromise, getCurrentTransaction, walletMoney, setWalletMoney }) => {

    const { enqueueSnackbar } = useSnackbar()
    const { company }: any = useCompany()
    const [showBank, setShowBank] = useState<any>(false);
    const [bankAccounts, setBankAccounts] = useState<any>({});
    const [openAddAmount, setOpenAddAmount] = useState(false);
		const [openVerifyBank, setOpenVerifyBank] = useState(false);
		const [accountId, setAccountId] = useState<number>(0);
    const [loading, setLoading] = useState<LoadingTypes>(budgetLoadingInitialState);
		const [deletingAccountId, setDeletingAccountId] = useState<any>(null);

		const ach_fee_type: any = company?.ach_fee_type;
		const ach_fee: any = company?.ach_fee;
		let fee = 0;
	
		let totalAmount = parseFloat(walletMoney);
		if (ach_fee_type === 'percentage') {
			fee = (parseFloat(walletMoney) * parseFloat(ach_fee)) / 100;
			totalAmount = parseFloat(walletMoney) + fee;
		} else if (ach_fee_type === 'fixed') {
			fee = parseFloat(ach_fee);
			totalAmount = parseFloat(walletMoney) + parseFloat(ach_fee);
		} 

		const [totalWalletMoney, setTotalWalletMoney] = useState<number>(totalAmount);

    const handleGenerateToken = async (bankAccountData) => {
			try {
				if (!stripePromise) {
					return { error: 'Stripe promise required' };
				}

				const { token, error } = await stripePromise.createToken('bank_account', bankAccountData);

				if (token) {
					// Use the generated token for payment or further processing
					return { token: token }
				} else {
					// Handle the error
					throw error;
				}
			} catch (error) {
				return { error: error }
			}
    };

    const { mutate: refetchBankAccounts } = useMutation(() => API.get('getBankAccountDetails'), {
			onSuccess: (response: any) => { !response?.errors && setBankAccounts(response) }
    })

    let payloadTmp:any = {};
    const { mutate: addBankAccount } = useMutation((payload: any) => {
			payloadTmp = payload;
			return API.create('addBankAccount', payload);
    }, {
			onSuccess: (response: any) => {
				if (!response?.errors) {
					if(payloadTmp.save_bank){
						enqueueSnackbar('Bank account added successfully.',
							{
								variant: 'success',
							},
						);
					}
					
					enqueueSnackbar(response?.message,
						{
							variant: response?.status === 'success' ? 'success' : 'error',
						},
					);

					setLoading(budgetLoadingInitialState);
					getCurrentTransaction();
					refetchBankAccounts();
					setWalletMoney(1.0);
					setTotalWalletMoney(totalAmount);
					setOpenAddBank(false);
				} else setLoading(budgetLoadingInitialState);
			},
    });

		const { mutate: verifyBankAccount } = useMutation((payload: any) => {
			payloadTmp = payload;
			return API.create('verifyBankAccount', payload);
		}, {
			onSuccess: (response: any) => {
				if (!response?.errors) {
					enqueueSnackbar(response?.message,
						{
							variant: response?.status === 'success' ? 'success' : 'error',
						},
					);

					refetchBankAccounts();
					setWalletMoney(1.0);
					setTotalWalletMoney(totalAmount);
					setLoading(budgetLoadingInitialState);
					setOpenVerifyBank(false);
				} else setLoading(budgetLoadingInitialState);
			},
    })

    const { mutate: chargeBankAccount } = useMutation((payload: any) => {
			payloadTmp = payload;
			// console.log("payloadTmp chargeBankAccount: ", payloadTmp);
			return API.create('chargeBankAccount', payload);
		}, {
			onSuccess: (response: any) => {
				if (!response?.errors) {
					enqueueSnackbar(response?.message,
						{
							variant: (response?.status === 'succeeded' || response?.status === 'pending') ? 'success' : 'error',
						},
					);

					getCurrentTransaction();
					refetchBankAccounts();
					setWalletMoney(1.0);
					setTotalWalletMoney(totalAmount);
					setLoading(budgetLoadingInitialState);
					setOpenAddAmount(false);
				} else setLoading(budgetLoadingInitialState);
			},
    })

    const deleteBankAccountMutation = useMutation(
			(payload: any) => API.delete('removeBankAccount', null,
				{
					queryParams: { company_id: company?.id },
					body: payload
				}),
			{
				onSettled: (response: any) => {
					if (!response.errors) {
						setBankAccounts({});
						enqueueSnackbar('Bank account removed successfully.',
							{
								variant: 'success',
							});
						refetchBankAccounts();
						addAmountForm.setValues({
							...addAmountForm.values, 
							total_amount: totalWalletMoney.toFixed(2),
						})
						setDeletingAccountId(null);
					}
				},
			},
    )

    const formik = useFormik({
			initialValues: budgetAddBankFormInitialState,
			validationSchema: validationSchema,
			onSubmit: async (values, { resetForm }) => {
				setLoading({
					...loading,
					addBankLoading: true
				});
				const { token, error }: any = await handleGenerateToken({
					country: 'US',
					currency: 'usd',
					routing_number: values.routing_number,
					account_number: values.account_number,
					account_holder_name: values.account_holder_name,
					account_holder_type: values.account_holder_type,
				})
				if (token) {
					await addBankAccount({
						token: token?.id,
						stripe_data: token,
						save_bank: values.save_bank,
					});

					// Then reset form
					resetForm();
				} else {
					setLoading(budgetLoadingInitialState);
					if (error?.code === "account_number_invalid") {
						enqueueSnackbar("Invalid account number",
							{
								variant: 'error',
							},
						)
						return;
					}
					enqueueSnackbar(error?.message,
						{
							variant: 'error',
						},
					)
				}
			},
    });

		const verifyBankForm = useFormik({
			initialValues: budgetVerifyBankFormInitialState,
			validationSchema: yup.object({
				amount1: yup
					.number()
					.transform((value, originalValue) => {
						if (originalValue === '' || originalValue == null) return undefined;
						const num = Number(originalValue);
						return isNaN(num) ? undefined : num;
					})
					.typeError("Must be a valid number")
					.min(0.01, "Amount must be at least 0.01")
					.required("This field is Required"),
				amount2: yup
					.number()
					.transform((value, originalValue) => {
						if (originalValue === '' || originalValue == null) return undefined;
						const num = Number(originalValue);
						return isNaN(num) ? undefined : num;
					})
					.typeError("Must be a valid number")
					.min(0.01, "Amount must be at least 0.01")
					.required("This field is Required"),
			}),
			onSubmit: async (values, { resetForm }) => {
				setLoading({
					...loading,
					verifyBankLoading: true
				});

				await verifyBankAccount({
					amount1: parseFloat(values?.amount1 || "0"),
					amount2: parseFloat(values?.amount2 || "0"),
					currency: "usd",
					bank_account_id: (bankAccounts?.bank_account?.data?.length) && bankAccounts?.bank_account?.data[accountId].id,
				});

				// Then reset form
				resetForm();
			},
    });

    const addAmountForm = useFormik({
			initialValues: {
				amount: "0",
				fee_type: '',
				fee_value: 0,
				total_amount: "0",
				fee_amount: "0",
			},
			validationSchema: yup.object({
				amount: yup.number().min(1).required("This field is Required"),
			}),
			onSubmit: async (values, { resetForm }) => {
				setLoading({
					...loading,
					addAmountLoading: true
				});

				await chargeBankAccount({
					amount: parseFloat(values?.amount),
					bank_account_id: (bankAccounts?.bank_account?.data?.length) && bankAccounts?.bank_account?.data[accountId].id,
					currency: "usd",
					fee_type: values?.fee_type,
					fee_value: values?.fee_value,
					total_amount: parseFloat(values?.total_amount || "0"),
					fee_amount: parseFloat(values?.fee_amount || "0"),
				})

				// Then reset form
				resetForm();
			},
    });

    useEffect(() => {
			setTotalWalletMoney(totalAmount);

			addAmountForm.setValues({ 
				amount: parseFloat(walletMoney).toFixed(2),
				fee_type: ach_fee_type,
				fee_value: ach_fee,
				total_amount: totalAmount.toFixed(2),
				fee_amount: fee.toFixed(2)
			})

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalAmount])
		
    useEffect(() => refetchBankAccounts(), [refetchBankAccounts]);

    useEffect(() => {
			if (get(bankAccounts, "bank_account.data") && get(bankAccounts, "bank_account.data").length) {
				setShowBank(true)
			} else setShowBank(false)
    }, [bankAccounts])

    return (
			<>
				<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} my={1}>
					<Typography variant='h6' component='h6' fontWeight={500}><b>Bank Accounts</b></Typography>
				</Box>

				{(!showBank) && (
					<Box sx={{ textAlign: "center", marginBottom: 3, border: '1px solid #607d8b', borderRadius: 3, p: 3 }}>
						<Typography variant='body2' component='span' fontWeight={500}>No Bank Accounts Found</Typography>
					</Box>
				)}

				{
					showBank && bankAccounts?.bank_account?.data.map((account: any, idx: number) => {
						return <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={3} sx={{ border: '1px solid #607d8b', borderRadius: 3, p: 1 }} key={account?.id}>
							<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
								<Typography variant='h6' color={'primary'} component='h6' fontWeight={700} style={{ marginRight: 7 }}>{account?.bank_name}</Typography>
								<Typography variant='body2' component='span' color={'info'} fontWeight={500}> **** {account?.last4} </Typography>
							</Box>
							<Box>
								{
									account?.status === 'new' && (
										<Button variant="contained" onClick={() => {
											setOpenVerifyBank(true);
											setAccountId(idx);
											verifyBankForm.setValues({ 
												...verifyBankForm.values, 
												amount1: parseFloat(walletMoney).toFixed(2),
												amount2: parseFloat(walletMoney).toFixed(2),
											})
										}}>Verify Bank Account</Button>
									)
								}

								{
									account?.status === 'verified' && (
										<Button variant="contained" onClick={() => {
											setOpenAddAmount(true);
											setAccountId(idx);
											setTotalWalletMoney(totalAmount);
											addAmountForm.setValues({ 
												...addAmountForm.values, 
												amount: parseFloat(walletMoney).toFixed(2),
												fee_type: ach_fee_type,
												fee_value: ach_fee,
												total_amount: totalAmount.toFixed(2),
												fee_amount: fee.toFixed(2)  
											});
										}}>Add Funds</Button>
									)
								}
								
								<LoadingButton
									type="button"
									loading={account?.id === deletingAccountId}
									autoFocus
									variant='text'
									color={'primary'}
									onClick={() => {
										setDeletingAccountId(account?.id);
										deleteBankAccountMutation.mutate({
											id: account?.id
										})
									}}
								>
									<ClearIcon />
									<span>Delete</span>
								</LoadingButton>
							</Box>
						</Box>
					})
				}

				{/* Add bank account dialog */}
				<DialogTeleport
					dialogOpen={openAddBank}
					handleDialogClose={() => setOpenAddBank(false)}
					dialogTitle="Bank Details"
					dialogSize={"sm"}
					isFullWidth={true}
				>
					<form onSubmit={formik.handleSubmit}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<TextField 
									name="account_holder_name" 
									label="Account Holder Name" 
									variant="outlined" 
									fullWidth 
									value={formik.values.account_holder_name}
									onChange={formik.handleChange}
									error={formik.touched.account_holder_name && Boolean(formik.errors.account_holder_name)}
									helperText={formik.touched.account_holder_name && formik.errors.account_holder_name} 
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									id="outlined-select-currency"
									select
									name="account_holder_type" label="Account Holder Type" variant="outlined" fullWidth value={formik.values.account_holder_type}
									onChange={formik.handleChange}
									error={formik.touched.account_holder_type && Boolean(formik.errors.account_holder_type)}
									helperText={formik.touched.account_holder_type && formik.errors.account_holder_type}
								>
									{budgetAccountHolderTypes.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>
							</Grid>
							<Grid item xs={12}>
								<TextField 
									name="account_number" 
									label="Account Number" 
									variant="outlined" 
									type='number' 
									fullWidth 
									value={formik.values.account_number}
									onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => formik.setValues({ ...formik.values, account_number: `${event.target.value}` })}
									error={formik.touched.account_number && Boolean(formik.errors.account_number)}
									helperText={formik.touched.account_number && formik.errors.account_number} 
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField 
									name="routing_number" 
									label="Routing Number" 
									variant="outlined" 
									fullWidth 
									value={formik.values.routing_number}
									onChange={formik.handleChange}
									error={formik.touched.routing_number && Boolean(formik.errors.routing_number)}
									helperText={formik.touched.routing_number && formik.errors.routing_number} 
								/>
							</Grid>
							<Grid item xs={12} sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: 'space-between'
							}}>
								<Box sx={{ width: "100%", textAlign: 'right' }}>
									<LoadingButton
										type="submit"
										loading={loading.addBankLoading}
										autoFocus
										variant='contained'
									>
										<span>Add Bank Account</span>
									</LoadingButton>
								</Box>
							</Grid>
						</Grid>
					</form>
				</DialogTeleport>

				{/* Verify Bank Account Dialog */}
				<DialogTeleport
					dialogOpen={openVerifyBank}
					handleDialogClose={() => setOpenVerifyBank(false)}
					dialogTitle="Verify Bank Account"
					dialogSize={"sm"}
					isFullWidth={true}
				>
					<form onSubmit={verifyBankForm.handleSubmit}>
						<Grid container>
							<Grid item xs={12} sx={{ textAlign: "center" }}>
								<Typography variant='body2' component='span' fontWeight={500}>We've made two small deposits into this account. Please verify the amount of each one.</Typography>
							</Grid>
							<Grid item xs={12}>
								<TextField
									data-cy='amount1'
									id='amount1'
									name="amount1"
									label="Amount #1"
									placeholder="Enter Amount #1"
									variant="outlined"
									fullWidth
									required
									value={verifyBankForm.values.amount1}
									onChange={verifyBankForm.handleChange}
									error={verifyBankForm.touched.amount1 && Boolean(verifyBankForm.errors.amount1)}
									helperText={verifyBankForm.touched.amount1 && verifyBankForm.errors.amount1}
									margin='normal'
									InputProps={{
										inputComponent: MoneyInput,
									}}
									InputLabelProps={{
										shrink: true,
									}}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									data-cy='amount2'
									id='amount2'
									name="amount2"
									label="Amount #2"
									placeholder="Enter Amount #2"
									variant="outlined"
									fullWidth
									required
									value={verifyBankForm.values.amount2}
									onChange={verifyBankForm.handleChange}
									error={verifyBankForm.touched.amount2 && Boolean(verifyBankForm.errors.amount2)}
									helperText={verifyBankForm.touched.amount2 && verifyBankForm.errors.amount2}
									margin='normal'
									InputProps={{
										inputComponent: MoneyInput,
									}}
									InputLabelProps={{
										shrink: true,
									}}
								/>
							</Grid>
							<Grid item xs={12} sx={{ mt: 2 }}>
								<Box textAlign={'end'}>
									<LoadingButton
											type="submit"
											loading={loading.verifyBankLoading}
											autoFocus
											variant='contained'
									>
										<span>Verify</span>
									</LoadingButton>
								</Box>
							</Grid>
						</Grid>
					</form>
				</DialogTeleport>

				{/* Add amount to bank dialog */}
				<DialogTeleport
					dialogOpen={openAddAmount}
					handleDialogClose={() => setOpenAddAmount(false)}
					dialogTitle="Add Funds"
					dialogSize={"sm"}
					isFullWidth={true}
				>
					<form onSubmit={addAmountForm.handleSubmit}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<TextField
									data-cy='amount'
									id='amount'
									name="amount"
									label="Reward Deposit Amount"
									placeholder="Enter Amount"
									variant="outlined"
									fullWidth
									required
									// value={addAmountForm.values.amount}
									// onChange={addAmountForm.handleChange}
									value={walletMoney}
									onChange={(event) => {
										setWalletMoney(parseFloat(event.target.value).toFixed(2));
										// formik.setFieldValue("username", e.target.value.trim())
										if (ach_fee_type === 'percentage') {
											const fee = (parseFloat(event.target.value) * ach_fee) / 100;
											const total = parseFloat(event.target.value) + fee;
											setTotalWalletMoney(total || 0);
											formik.setFieldValue("fee_type", ach_fee_type)
											formik.setFieldValue("fee_value", ach_fee)
											formik.setFieldValue("total_amount", total.toFixed(2))
											formik.setFieldValue("fee_amount", fee.toFixed(2))
											formik.setFieldValue("amount", parseFloat(event.target.value).toFixed(2))
										} else if (ach_fee_type === 'fixed') {
											const total = parseFloat(event.target.value) + ach_fee;
											setTotalWalletMoney(total || 0);
											formik.setFieldValue("amount", parseFloat(event.target.value).toFixed(2))
											formik.setFieldValue("fee_type", ach_fee_type)
											formik.setFieldValue("fee_value", ach_fee)
											formik.setFieldValue("total_amount", total.toFixed(2))
											formik.setFieldValue("fee_amount", ach_fee.toFixed(2))
										} else {
											setTotalWalletMoney(parseFloat(event.target.value));
											formik.setFieldValue("amount", parseFloat(event.target.value).toFixed(2))
											formik.setFieldValue("fee_type", ach_fee_type)
											formik.setFieldValue("fee_value", ach_fee)
											formik.setFieldValue("total_amount", parseFloat(event.target.value).toFixed(2))
											formik.setFieldValue("fee_amount", ach_fee.toFixed(2))
										}
									}}
									// error={addAmountForm.touched.amount && Boolean(addAmountForm.errors.amount)}
									// helperText={addAmountForm.touched.amount && addAmountForm.errors.amount}
									margin='normal'
									InputProps={{
										inputComponent: MoneyInput,
									}}
									InputLabelProps={{
										shrink: true,
									}}
								/>
							</Grid>
							<Grid item xs={12}>
								<Grid container mt={3}>
									<Grid item xs={5}>
										<Typography variant='h6' component='h6' fontWeight={500} textAlign={"end"} >
											Transaction Fee:
										</Typography>
									</Grid>
									<Grid item xs={7}>
										<Typography variant='h6' color={'#000000'} component='h6' fontWeight={600} textAlign={"end"} >
											{
												ach_fee_type === 'percentage' 
												? `${ach_fee}%` 
												: ach_fee_type === 'fixed' 
													? `$${ach_fee.toFixed(2)}` 
													: '$0.00'
											} 
										</Typography>
									</Grid>
			
									<Grid item xs={5}>
										<Typography variant='h6' component='h6' fontWeight={500} textAlign={"end"} >
											Transaction Total:
										</Typography>
									</Grid>
									<Grid item xs={7}>
										<Typography variant='h6' color={'#000000'} component='h6' fontWeight={600} textAlign={"end"}>
											{`$${totalWalletMoney.toFixed(2)}`}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={12}>
								<Box textAlign={'end'}>
									<LoadingButton
											type="submit"
											loading={loading.addAmountLoading}
											autoFocus
											variant='contained'
									>
										<span>Add Funds</span>
									</LoadingButton>
								</Box>
							</Grid>
						</Grid>
					</form>
				</DialogTeleport>
			</>
    )
}

export default ManageAddBankAccount